import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/shared/stores/auth'
import './types'

const router = createRouter({
    history: createWebHistory(),
    routes: [
        {
            path: '/',
            component: () => import('@/layouts/DefaultLayout.vue'),
            children: [
                {
                    path: '',
                    name: 'home',
                    component: () => import('@/Areas/Main/HomeView.vue'),
                },
                {
                    path: 'about',
                    name: 'about',
                    component: () => import('@/Areas/Main/AboutView.vue'),
                },
                {
                    path: 'lab',
                    name: 'lab',
                    component: () => import('@/Areas/Main/ProjectsView.vue'),
                },
                // Projects was renamed Lab; journal/blog were removed
                // entirely — keep old links landing somewhere sensible.
                { path: 'projects', redirect: { name: 'lab' } },
                { path: 'journal/:slug(.*)?', redirect: { name: 'home' } },
                { path: 'blog/:slug(.*)?', redirect: { name: 'home' } },
                {
                    path: 'shop',
                    name: 'shop',
                    component: () => import('@/Areas/Main/ShopView.vue'),
                },
                {
                    path: 'contact',
                    name: 'contact',
                    component: () => import('@/Areas/Main/ContactView.vue'),
                },
                // Dev-only living style guide for the form control library —
                // stripped from production builds entirely.
                ...(import.meta.env.DEV
                    ? [
                          {
                              path: 'dev/inputs',
                              name: 'dev-inputs',
                              component: () => import('@/Areas/Dev/InputsStyleGuide.vue'),
                          },
                      ]
                    : []),
                {
                    path: 'login',
                    name: 'login',
                    component: () => import('@/Areas/Account/LoginView.vue'),
                },
                {
                    path: 'forgot-password',
                    name: 'forgot-password',
                    component: () => import('@/Areas/Account/ForgotPasswordView.vue'),
                },
                {
                    path: 'reset-password',
                    name: 'reset-password',
                    component: () => import('@/Areas/Account/ResetPasswordView.vue'),
                },
                {
                    path: 'verify-email',
                    name: 'verify-email',
                    component: () => import('@/Areas/Account/VerifyEmailView.vue'),
                },
                {
                    path: 'unauthorized',
                    name: 'unauthorized',
                    component: () => import('@/shared/UnauthorizedView.vue'),
                },
                {
                    path: ':pathMatch(.*)*',
                    name: 'not-found',
                    component: () => import('@/shared/NotFound.vue'),
                },
            ],
        },
        {
            path: '/',
            component: () => import('@/layouts/AuthLayout.vue'),
            meta: { requiresAuth: true },
            children: [
                {
                    path: 'dashboard',
                    name: 'dashboard',
                    component: () => import('@/Areas/Account/DashboardView.vue'),
                },
                {
                    path: 'profile',
                    name: 'profile',
                    component: () => import('@/Areas/Account/ProfileView.vue'),
                },
                {
                    path: 'apps',
                    name: 'apps',
                    component: () => import('@/Areas/Account/AppsView.vue'),
                },
            ],
        },
    ],
})

router.beforeEach(async (to) => {
    const requiresAuth = to.matched.some((r) => r.meta.requiresAuth)
    const requiredRole = to.matched.find((r) => r.meta.role)?.meta.role

    if (!requiresAuth && !requiredRole) return true

    const auth = useAuthStore()
    if (!auth.loaded) await auth.refresh()

    if (!auth.isAuthenticated) {
        return { name: 'login', query: { redirect_to: to.fullPath } }
    }
    if (requiredRole && !auth.hasRole(requiredRole)) {
        return { name: 'unauthorized' }
    }
    return true
})

export default router
